import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../../../../hooks/use-notebook-config";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import {TaxManagementListDataGridColumnNames} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import {DefaultPaginationInfo} from "../../../../../../../../core/constants/enums";
import useSearch from "../../../../../../../hooks/use-search";
import {ReactComponent as Passed} from "../../../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../../../assets/images/validations/failed.svg";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {Add, DeleteOutlined, Edit} from "@mui/icons-material";
import Button from "../../../../../../../components/base/button/button";
import SearchInput from "../../../../../../../components/base/input/search_input";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import UpsertTaxDialog from "../../../../../../../components/dialogs/upsert-tax-dialog";
import {CircularProgress} from "@mui/material";
import Api from "../../../../../../../../core/services/api/api";
import useBehavioralAnalyticsTool from "../../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";

const exportingChunkSize = 50;

const dataGridSaveConfig = {
    name: 'TaxManagementListViewDataGrid',
    version: 1,
}

const initialUpsertDialogState = {open: false, data: null,};

const TaxManagementListView = () => {
    const [data, setData] = useState([]);
    const [upsertDialogState, setUpsertDialogState] = useState(initialUpsertDialogState);
    const [removingIds, setRemovingIds] = useState([]);
    const [fetchingAllTaxes, setFetchingAllTaxes] = useState(false);
    const [allTaxes, setAllTaxes] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('tax-management-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.taxesList);
        configureBehavioralTool((tags) => tags.taxesList);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of column in the data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            // {
            //     name: TaxManagementListDataGridColumnNames.remove,
            //     alignment: DataGridColumnAlignments.center,
            //     type: DataGridColumnTypes.element,
            //     sortable: false,
            //     pinnedToggleable: false,
            //     resizable: false,
            //     visibilityToggleable: false,
            //     reorderable: false,
            //     pinned: true,
            //     width: 0,
            // },
            {
                name: TaxManagementListDataGridColumnNames.edit,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                pinned: true,
                width: 0,
            },
            {
                title: "Code",
                name: TaxManagementListDataGridColumnNames.code,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
            },
            {
                title: "Name",
                name: TaxManagementListDataGridColumnNames.name,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 120,
            },
            {
                title: "Rate",
                name: TaxManagementListDataGridColumnNames.rate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
            },
            {
                title: "Provincial",
                name: TaxManagementListDataGridColumnNames.isProvincial,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
            },
        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Creates the request body for the api call
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        return {
            pagination: args.paginationInfo,
            query: args.filters?.keyword,
        }
    }, [])

    /**
     * Gets the list of taxes by calling the api regarding this action
     * If the function is being called for export, the pagination info is passed as the input
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args);
        const response = await Api.getSalesTaxes(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareApiRequest])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)


    /**
     * Fetches all the taxes of the system
     * @param pageLength
     * @return {Promise<*[]>}
     */
    const getAllTaxes = useCallback(async (pageLength) => {
        const _paginationInfo = {
            length: pageLength,
            pageSize: 100,
            currentPage: 1,
        };
        let entries = [];
        while (pageLength > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                continue;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res?.data?.items);
            if (!isMounted())
                return entries;
            _paginationInfo.currentPage++;
        }
        return entries;
    }, [fetchEntries, isMounted, searchData])

    /**
     * Opens the dialog for adding a new tax to the system
     */
    const openUpsertDialog = useCallback(async (data) => {
        if (!allTaxes?.length) {
            setFetchingAllTaxes(true);
            const _allTaxes = await getAllTaxes(searchData.paginationInfo?.length ?? 0);
            if (!isMounted())
                return;
            setFetchingAllTaxes(false);
            setAllTaxes(_allTaxes);
        }
        setUpsertDialogState({
            open: true,
            data: data ?? null,
        })
    }, [allTaxes?.length, getAllTaxes, isMounted, searchData.paginationInfo?.length])

    /**
     * Searches over list of taxes in the system by setting the query params according to the received keyword
     * @param keyword
     */
    const searchOverTaxes = (keyword) => {
        searchData.setFilters(prevState => ({
            ...prevState,
            keyword: keyword?.trim(),
        }))
    }

    /**
     * Returns the object for exporting the list of taxes by preparing the title and body of the csv file
     * @param list
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((list) => {
        return {
            title: `Taxes`,
            body: list?.map((e, index) => ({
                '': `${index + 1}`,
                'Code': e.code,
                'Name': e.name,
                'Rate': `${e.rate}%`,
                'Provincial': e.isProvincial,
            })) ?? [],
        }
    }, [])

    /**
     * Exports the list of taxes in the system by calling the search function with the pagination info
     * Calls the function for formatting the data
     * @return {Promise<*>}
     */
    const canExportTaxesList = operatorAccessLevels.packages.base.exportTaxesList;
    const exportToCsv = useCallback(async () => {
        return formatExportingData(await getAllTaxes(exportingChunkSize));
    }, [formatExportingData, getAllTaxes])


    /**
     * Removes a tax from the list of taxes.
     * @param driverIds list of drivers to be removed
     * @private
     */
    const removeTax = useCallback(async (tax) => {
        setRemovingIds(prevState => [...prevState, tax.id]);
        // TODO: call api to remove tax.
        const response = {
            resultFlag: true,
        }
        if (!isMounted())
            return;
        setRemovingIds(prevState => prevState.filter(id => id !== tax.id));
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => e.id !== tax.id));
            setAllTaxes(prevState => prevState.filter(e => e.id !== tax.id));
        }
    }, [isMounted])

    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     */
    const getRemoveConfirmation = useCallback((tax) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected tax?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeTax(tax),
        );
    }, [removeTax])

    /**
     * Updates the state with the updated tax information.
     * @type {(function(*): void)}
     */
    const onTaxUpserted = useCallback((tax) => {
        setData(prevState => {
            const index = prevState.findIndex(e => e.id === tax.id);
            if (index >= 0) {
                return prevState.map(e => e.id === tax.id ? {...e, ...tax} : e)
            }
            return [...prevState, tax];
        })
        setAllTaxes(prevState => {
            const index = prevState.findIndex(e => e.id === tax.id);
            if (index >= 0) {
                return prevState.map(e => e.id === tax.id ? {...e, ...tax} : e)
            }
            return [...prevState, tax];
        })
    }, [])

    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return data?.map(tax => {
            const removing = removingIds.includes(tax.id);
            return ({
                key: tax.id,
                data: tax,
                cells: {
                    [TaxManagementListDataGridColumnNames.remove]: (
                        <IconButton
                            onPressed={() => getRemoveConfirmation(tax)}
                            disabled={removing}
                            color={'paper.error'}
                        >
                            <DeleteOutlined/>
                        </IconButton>
                    ),
                    [TaxManagementListDataGridColumnNames.edit]: (
                        <IconButton
                            onPressed={() => openUpsertDialog(tax)}
                            disabled={removing || fetchingAllTaxes}
                            color={'paper.secondary'}
                        >
                            {
                                fetchingAllTaxes
                                    ? <CircularProgress size={20} className={"icon"}/>
                                    : <Edit className={"icon"}/>
                            }
                        </IconButton>
                    ),
                    [TaxManagementListDataGridColumnNames.code]: tax.code,
                    [TaxManagementListDataGridColumnNames.name]: tax.name,
                    [TaxManagementListDataGridColumnNames.rate]: `${tax.rate}%`,
                    [TaxManagementListDataGridColumnNames.isProvincial]: tax.isProvincial
                        ? <Passed/>
                        : <Failed/>,
                },
            })
        })
    }, [data, fetchingAllTaxes, getRemoveConfirmation, openUpsertDialog, removingIds])

    return (
        <>
            <div className={'drivers-list-view'}>
                <div className={'page-title-section'}>
                    <div/>
                    <Button
                        className={'d-flex align-items-center justify-content-center'}
                        variant={'outlined'}
                        color={'primary'}
                        disabled={fetchingAllTaxes}
                        onClick={() => openUpsertDialog(undefined)}
                    >
                        {
                            fetchingAllTaxes
                                ? <CircularProgress size={20}/>
                                : <Add className='icon small mr-2'/>
                        }
                        {
                            fetchingAllTaxes
                                ? <p className={'button-label'}>loading...</p>
                                : <p className={'button-label'}>Add a new tax</p>
                        }
                    </Button>
                </div>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    initialValue={searchData.filters?.keyword}
                    search={searchOverTaxes}
                    label={'Search'}
                />
                <div className={'mb-3'}/>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    getExportInfo={canExportTaxesList ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            <UpsertTaxDialog
                setOpen={() => setUpsertDialogState(initialUpsertDialogState)}
                open={upsertDialogState.open}
                data={upsertDialogState.data}
                taxes={allTaxes}
                onSuccess={onTaxUpserted}
            />
        </>
    );
}

export default TaxManagementListView;
